import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import { Share2, Download } from "lucide-react";

const T = {
  en: { share: "Share my twin", download: "Download card", noAvatar: "Generate an avatar first", readiness: "READINESS", mental: "MENTAL", physical: "PHYSICAL", risk: "INJURY RISK", speed: "TOP SPEED", vo2: "VO2", weight: "WEIGHT" },
  fr: { share: "Partager mon jumeau", download: "Télécharger la carte", noAvatar: "Génère d'abord un avatar", readiness: "FRAÎCHEUR", mental: "MENTAL", physical: "PHYSIQUE", risk: "RISQUE BLESSURE", speed: "VITESSE MAX", vo2: "VO2", weight: "POIDS" },
};

const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });

const roundRect = (ctx, x, y, w, h, r) => { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); };

// Draws a 1080x1350 share card (avatar + name + weekly scores / stats) on a canvas → PNG blob.
export async function renderShareCard({ avatarSrc, name, position, scores, stat, weekKey, t }) {
  const W = 1080, H = 1350;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#090C10"; ctx.fillRect(0, 0, W, H);
  // Avatar
  const img = await loadImg(avatarSrc);
  const size = Math.min(img.width, img.height);
  ctx.save(); roundRect(ctx, 60, 60, 960, 960, 40); ctx.clip();
  ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 60, 60, 960, 960); ctx.restore();
  const g = ctx.createLinearGradient(0, 600, 0, 1020); g.addColorStop(0, "rgba(9,12,16,0)"); g.addColorStop(1, "rgba(9,12,16,0.95)");
  ctx.save(); roundRect(ctx, 60, 60, 960, 960, 40); ctx.clip(); ctx.fillStyle = g; ctx.fillRect(60, 600, 960, 420); ctx.restore();
  // Brand
  ctx.fillStyle = "#00F076"; roundRect(ctx, 90, 90, 64, 64, 14); ctx.fill();
  ctx.fillStyle = "#000"; ctx.font = "900 44px sans-serif"; ctx.textBaseline = "middle"; ctx.fillText("P", 106, 124);
  ctx.fillStyle = "#fff"; ctx.font = "900 44px sans-serif"; ctx.fillText("TWIN", 170, 122);
  ctx.fillStyle = "#00F076"; ctx.fillText("SPORT", 290, 122);
  // Name
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#00F076"; ctx.font = "700 26px monospace"; ctx.fillText((position || "PLAYER").toUpperCase() + (weekKey ? `  ·  ${weekKey}` : ""), 100, 900);
  ctx.fillStyle = "#fff"; ctx.font = "900 84px sans-serif";
  let nm = (name || "Player").toUpperCase(); while (ctx.measureText(nm).width > 880) nm = nm.slice(0, -2) + "…";
  ctx.fillText(nm, 100, 985);
  // Score tiles
  const tiles = scores
    ? [[t.readiness, scores.readiness, "#00F076"], [t.mental, scores.mental, "#60a5fa"], [t.physical, scores.physical, "#FFB800"], [t.risk, scores.injury_risk, "#FF2E54"]]
    : [[t.speed, stat?.top_speed_kmh != null ? `${stat.top_speed_kmh}` : "—", "#00F076"], [t.vo2, stat?.endurance_vo2 ?? "—", "#60a5fa"], [t.weight, stat?.weight_kg != null ? `${stat.weight_kg}kg` : "—", "#FFB800"], ["TWIN", "AI", "#FF2E54"]];
  const tw = 222, gap = 24, x0 = 60, y0 = 1060;
  tiles.forEach(([lbl, val, col], i) => {
    const x = x0 + i * (tw + gap);
    ctx.fillStyle = "#121721"; roundRect(ctx, x, y0, tw, 230, 24); ctx.fill();
    ctx.strokeStyle = "#1E2638"; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = "#94A3B8"; ctx.font = "700 20px monospace"; ctx.fillText(lbl, x + 22, y0 + 50);
    ctx.fillStyle = col; ctx.font = "900 78px sans-serif"; ctx.fillText(String(val ?? "—"), x + 22, y0 + 150);
    if (typeof val === "number") { ctx.fillStyle = "#1E2638"; roundRect(ctx, x + 22, y0 + 185, tw - 44, 10, 5); ctx.fill(); ctx.fillStyle = col; roundRect(ctx, x + 22, y0 + 185, (tw - 44) * Math.min(100, val) / 100, 10, 5); ctx.fill(); }
  });
  ctx.fillStyle = "#475569"; ctx.font = "700 22px monospace"; ctx.fillText("MY AI TWIN · twinsport", 60, 1325);
  return new Promise((res) => c.toBlob(res, "image/png", 0.92));
}

export const ShareTwinButton = ({ avatarSrc, name, position, scores, stat, weekKey, size = "default" }) => {
  const { lang } = useLang();
  const t = T[lang] || T.en;
  const [busy, setBusy] = useState(false);

  const share = async () => {
    if (!avatarSrc) return toast.error(t.noAvatar);
    setBusy(true);
    try {
      const blob = await renderShareCard({ avatarSrc, name, position, scores, stat, weekKey, t });
      const file = new File([blob], "twinsport-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "twinsport", text: `${name} · My AI twin on twinsport` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "twinsport-card.png"; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success(t.download);
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Share failed"); }
    finally { setBusy(false); }
  };

  return (
    <Button size={size} onClick={share} disabled={busy || !avatarSrc} data-testid="share-twin-btn" className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
      {navigator.canShare ? <Share2 className="w-4 h-4 mr-2" /> : <Download className="w-4 h-4 mr-2" />}{busy ? "…" : t.share}
    </Button>
  );
};
